/* eslint-disable react/prop-types */
import { Descriptions } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { type CustomerListEndpointSpecsFragment } from '../../../../../api/fragments/CustomerListEndpointSpecs';
import {
    CreateCustomerListEndpointDocument,
    type CreateCustomerListEndpointMutation,
    type CreateCustomerListEndpointMutationVariables,
} from '../../../../../api/mutations/createCustomerListEndpoint';
import {
    UpdateCustomerListEndpointDocument,
    type UpdateCustomerListEndpointMutation,
    type UpdateCustomerListEndpointMutationVariables,
} from '../../../../../api/mutations/updateCustomerListEndpoint';
import { type CustomerListEndpointSettings } from '../../../../../api/types';
import InputField from '../../../../../components/fields/InputField';
import SelectField, { type SelectFieldProps } from '../../../../../components/fields/SelectField';
import { type EndpointImplementation } from './types';

const StyledBulletsList = styled.ul`
    margin-bottom: 0;
`;

const implementation: EndpointImplementation<CustomerListEndpointSpecsFragment, CustomerListEndpointSettings> = {
    getTitle: t => t('routerDetails:endpoints.CustomerListEndpoint.title'),
    ViewPanelComponent: ({ endpoint }) => (
        <Descriptions size="small" bordered>
            <Descriptions.Item label="Modules">
                {endpoint.applicationModules.length > 1 ? (
                    <StyledBulletsList>
                        {endpoint.applicationModules.map(module => (
                            <li key={module.id}>{module.displayName}</li>
                        ))}
                    </StyledBulletsList>
                ) : (
                    <div>{endpoint.applicationModules[0]?.displayName}</div>
                )}
            </Descriptions.Item>
        </Descriptions>
    ),
    getIsPrivate: () => true,
    FormComponent: ({ router }) => {
        const { t } = useTranslation(['routerDetails']);

        const options = useMemo(
            () =>
                router.company.modules.reduce<SelectFieldProps['options']>((acc, module) => {
                    if (
                        ['EventApplicationModule', 'StandardApplicationModule', 'ConfiguratorModule'].includes(
                            module.__typename
                        )
                    ) {
                        return [...acc, { value: module.id, label: module.displayName }];
                    }

                    return acc;
                }, []),
            [router]
        );

        return (
            <>
                <InputField
                    {...t('routerDetails:endpoints.fields.pathname', { returnObjects: true })}
                    name="pathname"
                />
                <SelectField
                    {...t('routerDetails:endpoints.fields.applicationModuleIds', { returnObjects: true })}
                    mode="multiple"
                    name="applicationModuleIds"
                    options={options}
                    showSearch
                />
                <InputField {...t('routerDetails:endpoints.fields.title', { returnObjects: true })} name="title" />
            </>
        );
    },
    getFormInitialValues: endpoint => ({
        pathname: endpoint.pathname,
        applicationModuleIds: endpoint.applicationModules.map(module => module.id),
        title: endpoint.title,
    }),
    submitCreation: (apolloClient, routerId, values) =>
        apolloClient.mutate<CreateCustomerListEndpointMutation, CreateCustomerListEndpointMutationVariables>({
            mutation: CreateCustomerListEndpointDocument,
            variables: { routerId, settings: values },
        }),
    submitUpdate: (apolloClient, routerId, endpointId, values) =>
        apolloClient.mutate<UpdateCustomerListEndpointMutation, UpdateCustomerListEndpointMutationVariables>({
            mutation: UpdateCustomerListEndpointDocument,
            variables: { routerId, endpointId, settings: values },
        }),
};

export default implementation;
